const CANVAS_SIZE = 600;
const HIT_RADIUS = 5;

const near = (x, y, targetX, targetY) =>
  x >= targetX - HIT_RADIUS &&
  x <= targetX + HIT_RADIUS &&
  y >= targetY - HIT_RADIUS &&
  y <= targetY + HIT_RADIUS;

const FLASH_COUNT = 8;
const FLASH_INTERVAL_MS = 70;
const EDITING = {
  drag: 'drag',
  curvePoint: 'curve point',
  curvePointReflection: 'curve point reflection',
};

const emptyTarget = () => ({ editing: null, cuttingAreaIndex: null, coordinateIndex: null });

export default class ManualRemovalPenTool {
  constructor(canvas, imageData, savedCuttingAreas) {
    this.canvas = canvas;
    this.context = canvas.getContext('2d');
    this.cursorPosition = { x: null, y: null };
    this.curvePaint = false;
    this.pathReadyToClose = false;
    this.deleteModifier = false;
    this.reset();

    if (imageData.height < CANVAS_SIZE && imageData.width < CANVAS_SIZE) {
      this.offsetX = CANVAS_SIZE / 2 - imageData.width / 2;
      this.offsetY = CANVAS_SIZE / 2 - imageData.height / 2;
      canvas.width = CANVAS_SIZE;
      canvas.height = CANVAS_SIZE;
    } else {
      this.offsetX = 0;
      this.offsetY = 0;
      canvas.width = imageData.width;
      canvas.height = imageData.height;
    }

    this.originalImage = document.createElement('canvas');
    this.originalImage.width = canvas.width;
    this.originalImage.height = canvas.height;
    this.originalImage.getContext('2d').putImageData(imageData, this.offsetX, this.offsetY);

    if (savedCuttingAreas?.length) {
      this.cuttingAreas = JSON.parse(JSON.stringify(savedCuttingAreas));
    }

    if (Array.isArray(this.cuttingAreas[0])) {
      this.#upgradeLegacyCuttingAreas();
    }
  }

  get previewUrl() {
    return this.originalImage.toDataURL();
  }

  get closedPaths() {
    return this.cuttingAreas.filter((cuttingArea) => cuttingArea.status === 'closed');
  }

  get hasOpenPath() {
    const latest = this.#latestCuttingArea;

    return latest.coordinates.length > 0 && latest.status === 'open';
  }

  reset() {
    this.cuttingAreas = [{ coordinates: [], status: 'open' }];
    this.target = emptyTarget();
  }

  press(event) {
    this.canvas.setPointerCapture(event.pointerId);
    this.#trackPointer(event);

    if (this.#latestCuttingArea.status === 'closed') {
      this.cuttingAreas.push({ coordinates: [], status: 'open' });
    }

    const latest = this.#latestCuttingArea;
    const { x, y } = this.cursorPosition;
    let createPoint = true;

    this.cuttingAreas.forEach((cuttingArea, cuttingAreaIndex) => {
      cuttingArea.coordinates.forEach((coordinate, coordinateIndex) => {
        if (!near(x, y, coordinate.x, coordinate.y)) {
          return;
        }

        createPoint = false;

        if (cuttingArea.status === 'closed') {
          this.target = { cuttingAreaIndex, coordinateIndex, editing: EDITING.drag };

          if (this.deleteModifier) {
            this.#deleteTargetedPoint();
          }
        } else if (coordinateIndex === 0 && latest.coordinates.length > 1) {
          this.pathReadyToClose = true;
          latest.coordinates.push({ x: latest.coordinates[0].x, y: latest.coordinates[0].y });
        }
      });
    });

    if (this.target.coordinateIndex !== null && this.target.editing === null) {
      const point = this.#targetedPoint;

      if (near(x, y, point.cpx, point.cpy)) {
        createPoint = false;
        this.target.editing = EDITING.curvePoint;
        this.curvePaint = false;
      } else if (near(x, y, point.x * 2 - point.cpx, point.y * 2 - point.cpy)) {
        createPoint = false;
        this.target.editing = EDITING.curvePointReflection;
        this.curvePaint = false;
      }
    }

    if (createPoint) {
      latest.coordinates.push({ x, y });
      this.target = emptyTarget();
    }

    if (latest.coordinates.length > 1) {
      this.curvePaint = true;
    }

    this.redraw();
  }

  move(event) {
    this.#trackPointer(event);
    this.redraw();

    const { x, y } = this.cursorPosition;

    if (this.target.editing === EDITING.drag) {
      const path = this.cuttingAreas[this.target.cuttingAreaIndex];
      const point = this.#targetedPoint;

      path.coordinates[0].x = path.coordinates[path.coordinates.length - 1].x;
      path.coordinates[0].y = path.coordinates[path.coordinates.length - 1].y;

      if (point.type === 'curve') {
        point.cpx = x - point.x + point.cpx;
        point.cpy = y - point.y + point.cpy;
      }
      point.x = x;
      point.y = y;
    } else if (this.target.editing === EDITING.curvePoint) {
      const point = this.#targetedPoint;

      point.cpx = x;
      point.cpy = y;
    } else if (this.target.editing === EDITING.curvePointReflection) {
      const point = this.#targetedPoint;

      point.cpx = point.x * 2 - x;
      point.cpy = point.y * 2 - y;
    } else if (this.curvePaint) {
      const latest = this.#latestCuttingArea;
      const point = latest.coordinates[latest.coordinates.length - 1];

      point.type = 'curve';
      point.cpx = point.x * 2 - x;
      point.cpy = point.y * 2 - y;
      this.#paintCurveHandle(point);
    }
  }

  // Returns whether this release closed the open path.
  release() {
    this.curvePaint = false;
    this.target.editing = null;

    const closed = this.pathReadyToClose;

    if (closed) {
      this.#latestCuttingArea.status = 'closed';
      this.target = emptyTarget();
      this.pathReadyToClose = false;
    }

    this.redraw();

    return closed;
  }

  flashOpenPath(done) {
    const latest = this.#latestCuttingArea;
    let flashes = 0;

    this.stopFlashing();
    this.flashTimer = setInterval(() => {
      latest.status = latest.status === 'flash' ? 'open' : 'flash';
      this.redraw();

      if (++flashes === FLASH_COUNT) {
        this.stopFlashing();
        done();
      }
    }, FLASH_INTERVAL_MS);
  }

  stopFlashing() {
    clearInterval(this.flashTimer);
  }

  redraw() {
    this.redrawPaths();

    if (this.target.coordinateIndex !== null) {
      this.#paintCurveHandle(this.#targetedPoint);
    }

    this.#paintHoverStates();
  }

  redrawPaths() {
    this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.context.drawImage(this.originalImage, 0, 0);
    this.#cutAreas();
    this.#paintGuideLine();
    this.#paintPathsAndPoints();
  }

  #trackPointer(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) * this.canvas.width) / rect.width;
    const y = ((event.clientY - rect.top) * this.canvas.height) / rect.height;

    this.cursorPosition.x = x - this.offsetX;
    this.cursorPosition.y = y - this.offsetY;
    this.deleteModifier = event.metaKey || event.ctrlKey;
  }

  #deleteTargetedPoint() {
    const path = this.cuttingAreas[this.target.cuttingAreaIndex];

    if (this.target.coordinateIndex === 0) {
      path.coordinates.splice(path.coordinates.length - 1, 1);
      path.coordinates[0].x = path.coordinates[path.coordinates.length - 1].x;
      path.coordinates[0].y = path.coordinates[path.coordinates.length - 1].y;
    } else if (this.target.coordinateIndex + 1 !== path.coordinates.length) {
      path.coordinates.splice(this.target.coordinateIndex, 1);
    }

    if (path.coordinates.length === 2) {
      this.cuttingAreas.splice(this.target.cuttingAreaIndex, 1);
    }

    this.target = emptyTarget();
  }

  #upgradeLegacyCuttingAreas() {
    const points = this.cuttingAreas;
    points.push(points[0]);

    this.cuttingAreas = [
      {
        coordinates: points.map(([x, y]) => ({ x, y })),
        status: 'closed',
      },
    ];
  }

  #tracePath(coordinates) {
    this.context.beginPath();
    coordinates.forEach((point) => {
      if (point.type === 'curve') {
        this.context.quadraticCurveTo(
          point.cpx + this.offsetX,
          point.cpy + this.offsetY,
          point.x + this.offsetX,
          point.y + this.offsetY
        );
      } else {
        this.context.lineTo(point.x + this.offsetX, point.y + this.offsetY);
      }
    });
  }

  #cutAreas() {
    this.context.save();
    this.cuttingAreas.forEach((cuttingArea, index) => {
      if (cuttingArea.status !== 'closed') {
        return;
      }

      if (index === 0) {
        this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.#tracePath(cuttingArea.coordinates);
        this.context.clip();
        this.context.drawImage(this.originalImage, 0, 0);
      } else {
        this.context.globalCompositeOperation = 'destination-out';
        this.#tracePath(cuttingArea.coordinates);
        this.context.fill();
      }
    });
    this.context.restore();
  }

  #paintGuideLine() {
    if (!this.hasOpenPath) {
      return;
    }

    const latest = this.#latestCuttingArea;
    const point = latest.coordinates[latest.coordinates.length - 1];

    this.context.strokeStyle = '#b22222';
    this.context.beginPath();
    this.context.moveTo(point.x + this.offsetX, point.y + this.offsetY);
    this.context.lineTo(this.cursorPosition.x + this.offsetX, this.cursorPosition.y + this.offsetY);
    this.context.stroke();
  }

  #paintCurveHandle(point) {
    if (point.type !== 'curve') {
      return;
    }

    const mirrorX = point.x * 2 - point.cpx;
    const mirrorY = point.y * 2 - point.cpy;

    this.context.beginPath();
    this.context.lineTo(point.cpx + this.offsetX, point.cpy + this.offsetY);
    this.context.lineTo(mirrorX + this.offsetX, mirrorY + this.offsetY);
    this.context.stroke();

    this.context.fillStyle = '#b22222';
    this.#paintDot(point.cpx, point.cpy, 2);
    this.#paintDot(mirrorX, mirrorY, 2);
    this.context.fillStyle = '#fff';
  }

  #paintDot(x, y, radius) {
    this.context.beginPath();
    this.context.arc(x + this.offsetX, y + this.offsetY, radius, 0, 2 * Math.PI);
    this.context.fill();
    this.context.stroke();
  }

  #paintHighlightedPoint(point, shadowColor) {
    this.context.shadowColor = shadowColor;
    this.context.fillStyle = '#fff';
    this.context.shadowBlur = 2;
    this.context.strokeStyle = 'rgba(237, 187, 116, .4)';
    this.context.lineWidth = 4;
    this.#paintDot(point.x, point.y, 5);
    this.context.lineWidth = 1;
    this.context.shadowBlur = 0;
  }

  #paintDeleteMarker(point) {
    const x = point.x + this.offsetX;
    const y = point.y + this.offsetY;

    this.context.strokeStyle = '#fff';
    this.context.lineWidth = 1;
    this.context.beginPath();
    this.context.moveTo(x - 3, y - 3);
    this.context.lineTo(x + 3, y + 3);
    this.context.moveTo(x - 3, y + 3);
    this.context.lineTo(x + 3, y - 3);
    this.context.stroke();
    this.context.beginPath();
    this.context.lineWidth = 0.5;
    this.context.arc(x, y, 6, 0, 2 * Math.PI);
    this.context.stroke();
    this.context.lineWidth = 1;
  }

  #paintHoverStates() {
    const { x, y } = this.cursorPosition;

    this.cuttingAreas.forEach((cuttingArea) => {
      cuttingArea.coordinates.forEach((point, index) => {
        if (!near(x, y, point.x, point.y)) {
          return;
        }

        if (this.deleteModifier && cuttingArea.status === 'closed') {
          this.#paintDeleteMarker(point);
        } else if (cuttingArea.status === 'closed' && this.target.editing !== EDITING.drag) {
          this.#paintHighlightedPoint(point, '#b22222');
        } else if (
          cuttingArea.status === 'open' &&
          index === 0 &&
          cuttingArea.coordinates.length > 1
        ) {
          this.#paintHighlightedPoint(point, '#48bb20');
        }
      });
    });

    if (this.target.editing === EDITING.drag) {
      this.#paintHighlightedPoint(this.#targetedPoint, '#b22222');
    }
  }

  #paintPathsAndPoints() {
    this.cuttingAreas.forEach((cuttingArea) => {
      if (cuttingArea.status === 'closed') {
        this.context.strokeStyle = '#000';
        this.context.fillStyle = '#000';
      } else if (cuttingArea.status === 'flash') {
        this.context.strokeStyle = '#fa8072';
        this.context.fillStyle = '#fff';
      } else {
        this.context.strokeStyle = '#b22222';
        this.context.fillStyle = '#fff';
      }

      this.#tracePath(cuttingArea.coordinates);
      this.context.stroke();

      cuttingArea.coordinates.forEach((point, index) => {
        if (cuttingArea.status !== 'closed' || index !== 0) {
          this.#paintDot(point.x, point.y, 4);
        }
      });
    });
  }

  get #latestCuttingArea() {
    return this.cuttingAreas[this.cuttingAreas.length - 1];
  }

  get #targetedPoint() {
    return this.cuttingAreas[this.target.cuttingAreaIndex].coordinates[this.target.coordinateIndex];
  }
}
